import React from 'react'
import styled from 'styled-components'
import { navigate } from 'gatsby'
import Button from '../components/button'
import colors from '../styles/colors'

function encode(data) {
  return Object.keys(data)
    .map(key => encodeURIComponent(key) + '=' + encodeURIComponent(data[key]))
    .join('&')
}

const TOTAL_STEPS = 3

const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5em;
  margin-bottom: 1em;
  font-size: 0.85em;
  color: #666;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`

const StepDot = styled.span`
  width: 28px;
  height: 4px;
  border-radius: 2px;
  background: ${props => (props.active ? colors.primary : '#ddd')};
  transition: background 0.2s ease;
`

const StepPane = styled.div`
  display: ${props => (props.active ? 'block' : 'none')};
`

const NavRow = styled.div`
  display: flex;
  gap: 0.75em;
  margin-top: 1em;
  button {
    flex: 1;
    padding: 0.75em;
  }
`

const BackButton = styled.button`
  padding: 0.75em;
  background: transparent;
  border: 2px solid ${colors.primary};
  color: ${colors.primary};
  font-weight: bold;
  font-family: 'Camber', -apple-system, BlinkMacSystemFont, Segoe UI, Roboto,
    Oxygen, Ubuntu, Cantarell, Fira Sans, Droid Sans, Helvetica Neue, sans-serif;
  cursor: pointer;
  border-radius: 0;
  &:hover {
    background: rgba(17, 52, 95, 0.08);
  }
`

const NextButton = styled.button`
  padding: 0.75em;
  background: ${colors.primary};
  border: 2px solid ${colors.primary};
  color: white;
  font-weight: bold;
  font-family: 'Camber', -apple-system, BlinkMacSystemFont, Segoe UI, Roboto,
    Oxygen, Ubuntu, Cantarell, Fira Sans, Droid Sans, Helvetica Neue, sans-serif;
  cursor: pointer;
  border-radius: 0;
  &:hover {
    background: #0c2645;
    border-color: #0c2645;
  }
`

const FormLabel = styled.label`
  color: #333;
`

const ButtonContainer = styled.div`
  margin-top: 1em;
  button {
    width: 100%;
    padding: 0.75em;
    margin-bottom: 1em;
  }
`

export default class ContactForm extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      step: 1,
      firstname: '',
      lastname: '',
      email: '',
      phone: '',
      eventDate: '',
      eventVenue: '',
      guestCount: '',
      eventTiming: '',
      eventType: '',
      description: '',
      subject: '',
    }
  }

  handleChange = e => {
    const { name, value } = e.target
    this.setState({ [name]: value }, () => {
      if (name === 'firstname' || name === 'lastname') {
        this.setState({
          subject:
            'BKSounds Website Form Submission - ' +
            this.state.firstname +
            ' ' +
            this.state.lastname,
        })
      }
    })
  }

  // Validate the inputs in the current step before allowing Next.
  validateStep = () => {
    const { step, firstname, lastname, email, phone, eventDate, eventVenue, guestCount, eventTiming, eventType } = this.state
    if (step === 1) {
      return firstname && lastname && email && phone
    }
    if (step === 2) {
      return eventDate && eventVenue && guestCount
    }
    if (step === 3) {
      return eventTiming && eventType
    }
    return true
  }

  focusNewStep = () => {
    if (typeof window === 'undefined' || !this.formEl) return

    const stepEl = this.formEl.querySelector(
      `[data-step="${this.state.step}"]`
    )
    const firstInput =
      stepEl && stepEl.querySelector('input, textarea, select')

    // Only scroll if the top of the form is above the viewport
    // (i.e. user can't currently see where the form starts).
    const rect = this.formEl.getBoundingClientRect()
    const formIsOffscreen = rect.top < 0

    const prefersReducedMotion =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    if (formIsOffscreen) {
      const top = rect.top + window.pageYOffset - 20
      window.scrollTo({
        top,
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
      })
    }

    // Focus the first input of the new step. preventScroll avoids
    // fighting the scrollTo above when the form was offscreen.
    if (firstInput) {
      const focusOpts = { preventScroll: formIsOffscreen }
      if (formIsOffscreen) {
        // Wait for the smooth scroll to (mostly) finish before focusing,
        // otherwise iOS will yank scroll position back to the input.
        setTimeout(() => firstInput.focus(focusOpts), 350)
      } else {
        firstInput.focus(focusOpts)
      }
    }
  }

  goNext = e => {
    e.preventDefault()
    // Trigger native browser validation on visible inputs in the current step.
    const formEl = this.formEl
    if (formEl) {
      const stepEl = formEl.querySelector(`[data-step="${this.state.step}"]`)
      if (stepEl) {
        const inputs = stepEl.querySelectorAll('input, textarea, select')
        for (let i = 0; i < inputs.length; i++) {
          if (!inputs[i].checkValidity()) {
            inputs[i].reportValidity()
            return
          }
        }
      }
    }
    this.setState(
      prev => ({ step: Math.min(prev.step + 1, TOTAL_STEPS) }),
      this.focusNewStep
    )
  }

  goBack = e => {
    e.preventDefault()
    this.setState(
      prev => ({ step: Math.max(prev.step - 1, 1) }),
      this.focusNewStep
    )
  }

  handleSubmit = e => {
    e.preventDefault()
    const form = e.target
    const { step, ...payload } = this.state
    fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: encode({
        'form-name': form.getAttribute('name'),
        ...payload,
      }),
    })
      .then(() =>
        navigate(form.getAttribute('action'), { state: { ...payload } })
      )
      .catch(error => alert(error))
  }

  render() {
    const { step } = this.state
    const showLabels = this.props.showLabels
    const showPlaceHolders = this.props.showPlaceHolders

    return (
      <form
        ref={el => (this.formEl = el)}
        name="contact-form"
        method="post"
        action="/thanks/"
        data-netlify="true"
        data-netlify-honeypot="bot-field"
        onSubmit={this.handleSubmit}
        className={
          this.props.style ? this.props.style : 'contact-form-default-style'
        }
        style={{ margin: '0' }}
      >
        <StepIndicator>
          <span>Step {step} of {TOTAL_STEPS}</span>
          <StepDot active={step >= 1} />
          <StepDot active={step >= 2} />
          <StepDot active={step >= 3} />
        </StepIndicator>

        {/* STEP 1 — Contact info */}
        <StepPane active={step === 1} data-step="1">
          <div className="form-group">
            <div className="row">
              <div className="col-12 col-lg-6">
                {showLabels ? (
                  <FormLabel htmlFor="firstname">First Name</FormLabel>
                ) : null}
                <input
                  type="text"
                  name="firstname"
                  className="form-control"
                  id="firstname"
                  placeholder={showPlaceHolders ? 'First Name' : ''}
                  required
                  value={this.state.firstname}
                  onChange={this.handleChange}
                />
              </div>
              <div className="col-12 col-lg-6">
                {showLabels ? (
                  <FormLabel htmlFor="lastname">Last Name</FormLabel>
                ) : null}
                <input
                  type="text"
                  name="lastname"
                  className="form-control"
                  id="lastname"
                  placeholder={showPlaceHolders ? 'Last Name' : ''}
                  required
                  value={this.state.lastname}
                  onChange={this.handleChange}
                />
              </div>
            </div>
          </div>

          <div className="form-group">
            <div className="row">
              <div className="col-12 col-lg-6">
                {showLabels ? (
                  <FormLabel htmlFor="email">Email address</FormLabel>
                ) : null}
                <input
                  type="email"
                  name="email"
                  id="email"
                  className="form-control"
                  placeholder={showPlaceHolders ? 'Email address' : ''}
                  required
                  value={this.state.email}
                  onChange={this.handleChange}
                />
              </div>
              <div className="col-12 col-lg-6">
                {showLabels ? (
                  <FormLabel htmlFor="phone">Phone</FormLabel>
                ) : null}
                <input
                  type="tel"
                  name="phone"
                  className="form-control"
                  id="phone"
                  placeholder={showPlaceHolders ? 'Phone' : ''}
                  required
                  value={this.state.phone}
                  onChange={this.handleChange}
                />
              </div>
            </div>
          </div>
        </StepPane>

        {/* STEP 2 — Event details */}
        <StepPane active={step === 2} data-step="2">
          <div className="form-group">
            {showLabels ? (
              <FormLabel htmlFor="eventDate">
                Event Date (YYYY-MM-DD)
              </FormLabel>
            ) : null}
            <input
              type="date"
              name="eventDate"
              id="eventDate"
              className="form-control"
              placeholder={showPlaceHolders ? 'Event Date' : ''}
              required
              value={this.state.eventDate}
              onChange={this.handleChange}
            />
          </div>

          <div className="form-group">
            {showLabels ? (
              <FormLabel htmlFor="eventVenue">
                Event Venue &amp; Location
              </FormLabel>
            ) : null}
            <input
              type="text"
              name="eventVenue"
              id="eventVenue"
              className="form-control"
              placeholder={showPlaceHolders ? 'Event Venue & Location' : ''}
              required
              value={this.state.eventVenue}
              onChange={this.handleChange}
            />
          </div>

          <div className="form-group">
            {showLabels ? (
              <FormLabel htmlFor="guestCount">
                Estimated Guest Count
              </FormLabel>
            ) : null}
            <input
              type="number"
              min="1"
              name="guestCount"
              id="guestCount"
              className="form-control"
              placeholder={showPlaceHolders ? 'Estimated Guest Count' : ''}
              required
              value={this.state.guestCount}
              onChange={this.handleChange}
            />
          </div>
        </StepPane>

        {/* STEP 3 — Event type + message */}
        <StepPane active={step === 3} data-step="3">
          <div className="form-group">
            {showLabels ? (
              <FormLabel htmlFor="eventTiming">Timing of Your Event</FormLabel>
            ) : null}
            <input
              type="text"
              name="eventTiming"
              id="eventTiming"
              className="form-control"
              placeholder={
                showPlaceHolders ? 'e.g. 6:00 PM – 11:00 PM' : ''
              }
              required
              value={this.state.eventTiming}
              onChange={this.handleChange}
            />
          </div>

          <div className="form-group">
            {showLabels ? (
              <FormLabel htmlFor="eventType">
                What Type of Event Are You Hosting?
              </FormLabel>
            ) : null}
            <input
              type="text"
              name="eventType"
              id="eventType"
              className="form-control"
              placeholder={
                showPlaceHolders ? 'Wedding, Corporate, Birthday, etc.' : ''
              }
              required
              value={this.state.eventType}
              onChange={this.handleChange}
            />
          </div>

          <div className="quote-form-label-input-group">
            {showLabels ? (
              <FormLabel htmlFor="description">
                Tell us about your project
              </FormLabel>
            ) : null}
            <textarea
              className="form-control"
              name="description"
              id="description"
              placeholder={
                showPlaceHolders ? 'Tell us about your project' : ''
              }
              rows="3"
              value={this.state.description}
              onChange={this.handleChange}
            />
          </div>
        </StepPane>

        {/* Hidden meta fields */}
        <input type="hidden" name="form-name" value="contact-form" />
        <p hidden>
          <label>
            Don’t fill this out:{' '}
            <input name="bot-field" onChange={this.handleChange} />
          </label>
        </p>
        <input
          type="hidden"
          name="subject"
          value={this.state.subject}
          onChange={this.handleChange}
          readOnly
        />

        <div data-netlify-recaptcha />

        {/* Navigation */}
        {step < TOTAL_STEPS ? (
          <NavRow>
            {step > 1 ? (
              <BackButton type="button" onClick={this.goBack}>
                Back
              </BackButton>
            ) : null}
            <NextButton type="button" onClick={this.goNext}>
              Next
            </NextButton>
          </NavRow>
        ) : (
          <>
            <NavRow>
              <BackButton type="button" onClick={this.goBack}>
                Back
              </BackButton>
            </NavRow>
            <ButtonContainer>
              <Button
                role="submit"
                type="submit"
                color="primary"
                className="shadow-lg"
              >
                Get FREE Estimate
              </Button>
            </ButtonContainer>
          </>
        )}
      </form>
    )
  }
}
